import React, { useState } from 'react';
import MuiGrid from '@material-ui/core/Grid';
import MuiBox from '@material-ui/core/Box';
import MuiButton from '@material-ui/core/Button';
import sortBy from 'lodash/sortBy';
import { withStyles } from '@material-ui/core/styles';
import * as API from 'api';
import { useQuery } from 'react-query';
import { dataApiUrl } from 'utils/url';
import AuthAxios from 'utils/authAxios';
import AddRemoveTrack from './AddRemoveTrack';
import AccountDetails from './AccountDetails';
import ReviewSplits from './ReviewSplits';

const DataApiUrl = dataApiUrl();
const RecoupAccounts = API.RecoupAccounts();
const RecoupAccountTracks = API.RecoupAccountTracks();
const RecoupAccountPassthroughs = API.RecoupAccountPassthroughs();
const styles = () => ({
  bold: {
    fontWeight: 'bold',
  },
  formElement: {
    margin: '6px',
    padding: '6px',
    width: '100%',
  },
});
function RecoupAccountForm({ ...props }) {
  const { goBack, saveRecoupAccount, account, onError } = props;

  const { data: payees } = useQuery(['payees-labels'], () =>
    // Sort Payee labels alphabetically
    RecoupAccounts.getAllPayeeLabels().then(data => sortBy(data, ['name']))
  );
  const [formData, setFormData] = useState({
    ...account,
    accountTracks: account?.accountTracks?.map(aT => ({ value: aT.track.id })),
    passThroughs: [],
  });

  function updateForm({ target: { value, id } }) {
    setFormData({ ...formData, [id]: value });
  }

  async function manageAddDeleteTracks(tracksFromForm) {
    const { accountTracks: currentTracks, id: recoup_account_id } = account;

    await Promise.all(
      currentTracks
        .filter(c => !tracksFromForm.find(t => Number(t.id || t.value) === Number(c.asset_track_id)))
        .map(c => RecoupAccountTracks.del(c.id))
    );
    await Promise.all(
      tracksFromForm
        .filter(c => !currentTracks.find(t => Number(c.id || c.value) === Number(t.asset_track_id)))
        .map(track => RecoupAccountTracks.create(recoup_account_id, track.id))
    );
  }

  async function manageAddDeletePassthroughs(splitIdsFromForm) {
    const { accountTracks: currentTracks, id: recoup_account_id } = account;

    //TECH DEbtish: THis is a lot of looping. Possible just doing query may be faster / better.

    //This gets all of the passthroughs currently associated with this recoupment accout
    //and acts like ones that are connected to a different recoupment account do not exists.
    const allPassthroughs = currentTracks.flatMap(Rtrack =>
      Rtrack.track.stakeholderTrackSplits
        .filter(split =>
          split.recoupAccountPassthroughs.some(p => String(p.recoup_account_id) === String(recoup_account_id))
        )
        .flatMap(split => split.recoupAccountPassthroughs)
    );

    const toDelete = allPassthroughs.filter(pt => !splitIdsFromForm.includes(pt.stakeholder_track_split_id));
    const toAdd = splitIdsFromForm.filter(
      splitId => !allPassthroughs.find(pt => pt.stakeholder_track_split_id === splitId)
    );

    //ForRemoval
    await Promise.all(toDelete.map(pt => RecoupAccountPassthroughs.del(pt.id)));
    await Promise.all(toAdd.map(splitId => RecoupAccountPassthroughs.create(recoup_account_id, splitId)));
  }

  async function prepareSave() {
    const {
      accountTracks = [],
      passThroughs = [],
      ledgers: _ledgers,
      user_group_id,
      committed_usd,
      name,
      open_date,
    } = formData;

    try {
      const passThroughIds = passThroughs.flatMap(pt => pt.passThroughSplitIds);
      if (!account) {
        await AuthAxios.post(`${DataApiUrl}/admin/recoupAccounts/full`, {
          account: {
            user_group_id,
            committed_usd,
            name,
            open_date,
            account_stage: 'Platform',
          },
          tracks: accountTracks.map(track => ({
            asset_track_id: track.id || track.value,
          })),
          passthroughs: passThroughIds.map(stakeholder_track_split_id => ({
            stakeholder_track_split_id,
          })),
        }).catch(err => {
          throw err.response;
        });
      } else {
        await manageAddDeleteTracks(accountTracks);
        await manageAddDeletePassthroughs(passThroughIds);
        await saveRecoupAccount({ name, committed_usd });
      }
      goBack();
    } catch (caughtError) {
      const status = caughtError?.status;
      const detail = caughtError?.data?.detail;

      if (status === 409) {
        onError(new Error('Recoupment account already exists for this label. Please use a different name.'));
        return;
      }

      const error = new Error(
        detail || caughtError?.message || caughtError?.data || 'Failed to save recoupment account.'
      );
      onError(error);
    }
  }

  function findLabelId() {
    return payees.find(p => p.user_group_id === formData.user_group_id)?.user_group_id;
  }

  return (
    <>
      <AccountDetails account={account} updateForm={updateForm} formData={formData} payees={payees} />
      {formData.user_group_id && payees && (
        <AddRemoveTrack updateForm={updateForm} formData={formData} labelId={findLabelId()} />
      )}
      {formData.accountTracks && formData.accountTracks.length && (
        <ReviewSplits
          trackIds={
            //the id from DuelBox is value. If untouched it will remain value
            formData.accountTracks.map(aT => aT.id || aT.value)
          }
          formData={formData}
          updateForm={updateForm}
        />
      )}
      <MuiGrid item xs={12}>
        <MuiBox>
          <MuiButton onClick={goBack}>Cancel</MuiButton>
          <MuiButton variant="contained" size="small" onClick={prepareSave}>
            Save
          </MuiButton>
        </MuiBox>
      </MuiGrid>
    </>
  );
}

export default withStyles(styles)(RecoupAccountForm);
